import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useParams } from 'react-router-dom';
import { useContext } from 'react';
import { store } from 'components/Store';
dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);

const useCreateParliamentLiveFilters = () => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { realtimeEvents } = state ?? {};
  const params = useParams();
  const { event } = params ?? {};

  const dateParam = getUrlParam('startDateTime');
  const parseStartDateTime = (startDateTime) => {
    let utcString;
    if (/^\d{4}-\d{2}-\d{2}$/.test(startDateTime)) {
      utcString = `${startDateTime}T00:00:00Z`;
    } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(startDateTime)) {
      utcString = `${startDateTime}:00Z`;
    } else if (/Z$/.test(startDateTime)) {
      utcString = startDateTime;
    } else {
      utcString = `${startDateTime}Z`;
    }
    return dayjs.utc(utcString);
  };

  const dayJsDateParam = dateParam ? parseStartDateTime(dateParam) : null;
  const currentUtcDateTime = dateParam ? dayJsDateParam : dayjs.utc();

  const currentUKDateTime = ({ specificDate }) =>
    specificDate && !dateParam ? dayjs(specificDate) : currentUtcDateTime.tz('Europe/London');

  const createUKDate = ({ specificDate }) => {
    let currentUKDate = currentUKDateTime({ specificDate }).tz('Europe/London');
    const start = currentUKDate.startOf('day').tz('Europe/London');
    const end = start.add(3, 'hour');
    if (currentUKDate.isBetween(start, end, null, '[)')) {
      currentUKDate = start.subtract(1, 'day').tz('Europe/London');
    }

    return currentUKDate;
  };

  const createParliamentLiveFilters = ({ specificDate }) => {
    const currentUKDate = createUKDate({ specificDate });
    const initialCallFirstDateValue = specificDate
      ? dayjs(specificDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss')
      : currentUKDate.format('HH:mm:ss') === '00:00:00'
        ? currentUKDate.format('YYYY-MM-DDTHH:mm:ss')
        : currentUKDate.utc()?.startOf('day').format('YYYY-MM-DDTHH:mm:ss');

    const initialCallSecondDateValue =
      specificDate && !dateParam
        ? dayjs(specificDate).endOf('day')?.format('YYYY-MM-DDTHH:mm:ss')
        : currentUtcDateTime.format('YYYY-MM-DDTHH:mm:ss');

    let parliamentLiveFilters = [
      {
        field: 'createdAtUtc',
        value: initialCallFirstDateValue,
        operator: 'DATE_GTE',
      },
      {
        field: 'createdAtUtc',
        value: initialCallSecondDateValue,
        operator: 'date_lt',
      },
    ];

    if (!event) {
      parliamentLiveFilters = [
        ...parliamentLiveFilters,
        {
          field: 'eventStartDateTime',
          value: initialCallFirstDateValue,
          operator: 'DATE_GTE',
        },
      ];
    }
    return { parliamentLiveFilters, initialCallFirstDateValue, initialCallSecondDateValue };
  };

  const realtimeEventsHaveNotStarted = () => {
    const now = currentUKDateTime({});
    const condition = realtimeEvents?.every((event, index) => {
      const eventDate = dayjs.tz(event?.scheduledStartDateTime, 'Europe/London');
      return eventDate.isAfter(now);
    });
    return condition;
  };

  return {
    createParliamentLiveFilters,
    createUKDate,
    dayJsDateParam,
    currentUKDateTime,
    currentUtcDateTime,
    realtimeEventsHaveNotStarted,
  };
};

export default useCreateParliamentLiveFilters;
